import { Fragment, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Pencil, Trash2, Save, Calculator as CalcIcon, Lock, Unlock, AlertTriangle, Archive, ArchiveRestore } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Calculator } from '../components/Calculator'
import { useActiveTenant } from '../contexts/tenant'
import { cn } from '../lib/cn'

type EditItem = { id: string; productName: string; boxes: string; units: string }

type Pallet = {
  id: string; code: string; type: string; status: string
  tenantId: string; locationId: string | null; locationCode: string | null
  quantityBoxes: number; quantityUnits: number; expiryDate: string | null
  notes: string | null; createdAt: string; openedAt: string | null; archivedAt: string | null
}

const statusVariant: Record<string, 'default' | 'info' | 'warning' | 'danger' | 'success'> = {
  empty: 'default', in_use: 'info', quarantine: 'warning', damaged: 'danger',
}
const statusLabel: Record<string, string> = {
  empty: 'Vazio', in_use: 'Em uso', quarantine: 'Quarentena', damaged: 'Danificado',
}
const typeLabel: Record<string, string> = { pallet: 'Palete', box: 'Caixa' }

export function PalletsPage() {
  const tenantId = useActiveTenant()
  const qc = useQueryClient()
  const [downloading, setDownloading] = useState<string | null>(null)
  const [formatFilter, setFormatFilter] = useState<'10x15' | 'a4'>('10x15')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editing, setEditing] = useState<string | null>(null)
  const [editItems, setEditItems] = useState<EditItem[]>([])
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [calcFor, setCalcFor] = useState<string | null>(null)

  // Abrir/fechar palete é COMANDO, não um checkbox escondido na correção de
  // contagem. Fechar tem regra no servidor: palete com caixa começada só lacra
  // com motivo — lacrar é dizer "não há nada começado aqui", e um palete que
  // fica aberto por engano trava a fila do abastecimento para sempre.
  const [estadoBusy, setEstadoBusy] = useState<string | null>(null)
  const [estadoMsg, setEstadoMsg] = useState<string | null>(null)
  const [verAbertos, setVerAbertos] = useState(false)

  // Arquivar: palete que acabou sai da lista sem perder o histórico. Apagar não
  // serve — ele é a prova de onde a mercadoria esteve (conferências,
  // abastecimentos, a nota que o trouxe).
  const [arquivados, setArquivados] = useState<'nao' | 'sim' | 'todos'>('nao')
  const [arqBusy, setArqBusy] = useState<string | null>(null)
  const [arqMsg, setArqMsg] = useState<string | null>(null)

  async function arquivar(p: { id: string; code: string }, arquivar: boolean) {
    setArqBusy(p.id); setArqMsg(null)
    try {
      await api.post(`/pallets/${p.id}/arquivar`, { arquivar, tenantId })
      setArqMsg(arquivar ? `${p.code} arquivado.` : `${p.code} voltou para a lista.`)
      await qc.invalidateQueries({ queryKey: ['pallets', tenantId] })
      await qc.invalidateQueries({ queryKey: ['pallets-vazios', tenantId] })
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      const t = err.response?.data?.title ?? 'Falha ao arquivar'
      setArqMsg(t); window.alert(t)
    } finally { setArqBusy(null) }
  }

  const { data: vazios } = useQuery<{ vazios: number }>({
    queryKey: ['pallets-vazios', tenantId],
    queryFn: async () => (await api.get('/pallets/vazios', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  async function arquivarTodosVazios() {
    if (!tenantId || !vazios?.vazios) return
    if (!window.confirm(`Arquivar ${vazios.vazios} palete(s) vazio(s)?\n\nEles saem da lista mas continuam no histórico — dá para desarquivar depois.`)) return
    setArqBusy('todos'); setArqMsg(null)
    try {
      const { data } = await api.post<{ arquivados: number }>('/pallets/arquivar-vazios', { tenantId })
      setArqMsg(`${data.arquivados} palete(s) arquivado(s).`)
      await qc.invalidateQueries({ queryKey: ['pallets', tenantId] })
      await qc.invalidateQueries({ queryKey: ['pallets-vazios', tenantId] })
    } catch (e) {
      const err = e as { response?: { data?: { title?: string } } }
      setArqMsg(err.response?.data?.title ?? 'Falha ao arquivar os vazios')
    } finally { setArqBusy(null) }
  }

  async function mudarEstado(p: { id: string; code: string; openedAt: string | null }, acao: 'abrir' | 'fechar', forcarComMotivo?: string) {
    setEstadoBusy(p.id); setEstadoMsg(null)
    try {
      const body: Record<string, unknown> = { acao, tenantId }
      if (forcarComMotivo) { body.forcar = true; body.motivo = forcarComMotivo }
      const { data } = await api.post(`/pallets/${p.id}/estado`, body)
      setEstadoMsg(acao === 'abrir'
        ? `${p.code} marcado como ABERTO.`
        : data.vazio
          ? `${p.code} fechado — estava vazio e saiu da fila do abastecimento.`
          : `${p.code} lacrado${data.forcado ? ' (forçado)' : ''}.`)
      await qc.invalidateQueries({ queryKey: ['pallets', tenantId] })
      await qc.invalidateQueries({ queryKey: ['pallets-abertos', tenantId] })
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { title?: string } } }
      const titulo = err.response?.data?.title ?? 'Falha ao mudar o estado do palete'
      // 409 = tem caixa começada. Não é erro do operador: é a pergunta certa na
      // hora certa — ou ele termina a caixa, ou assume e justifica.
      if (err.response?.status === 409 && acao === 'fechar' && !forcarComMotivo) {
        const motivo = window.prompt(`${titulo}\n\nSe ainda assim é para lacrar, escreva o motivo (fica no histórico do palete):`)
        if (motivo && motivo.trim().length >= 5) { setEstadoBusy(null); return mudarEstado(p, 'fechar', motivo.trim()) }
        if (motivo !== null && motivo.trim().length < 5) setEstadoMsg('Motivo muito curto — nada foi alterado.')
        else setEstadoMsg(null)
      } else {
        // A mensagem do topo some do campo de visão quando a linha está lá
        // embaixo: falha tem que aparecer onde o dedo está.
        setEstadoMsg(titulo)
        window.alert(titulo)
      }
    } finally { setEstadoBusy(null) }
  }

  function toggle(id: string) {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  async function openEdit(id: string) {
    if (editing === id) { setEditing(null); return }
    setEditing(id)
    setEditItems([])
    const { data } = await api.get(`/pallets/${id}`)
    setEditItems((data.items ?? []).map((it: any) => ({
      id: it.id,
      productName: it.productName ?? it.sku ?? '—',
      boxes: String(it.quantityBoxes ?? 0),
      units: String(it.quantityUnits ?? 0),
    })))
  }

  function setItem(idx: number, patch: Partial<EditItem>) {
    setEditItems(a => a.map((x, i) => (i === idx ? { ...x, ...patch } : x)))
  }

  async function saveEdit(id: string) {
    setSaving(true)
    try {
      await api.patch(`/pallets/${id}/items`, {
        // Sem `opened`: abrir/fechar virou comando próprio (cadeado na linha).
        // Mandar o estado junto da contagem abria uma segunda porta para mexer
        // nele sem querer — e sem motivo registrado.
        items: editItems.map(i => ({ id: i.id, quantityBoxes: Number(i.boxes) || 0, quantityUnits: Number(i.units) || 0 })),
      })
      await qc.invalidateQueries({ queryKey: ['pallets', tenantId] })
      setEditing(null)
    } finally {
      setSaving(false)
    }
  }

  async function removePallet(p: Pallet) {
    if (!window.confirm(`Apagar o palete ${p.code}? Remove o palete e suas caixas do armazém. Não dá para desfazer.`)) return
    setDeleting(p.id)
    try {
      await api.delete(`/pallets/${p.id}`, { params: { tenantId } })
      if (editing === p.id) setEditing(null)
      await qc.invalidateQueries({ queryKey: ['pallets', tenantId] })
    } finally {
      setDeleting(null)
    }
  }

  const { data: pallets = [], isLoading } = useQuery<Pallet[]>({
    queryKey: ['pallets', tenantId, arquivados],
    queryFn: async () => {
      if (!tenantId) return []
      return (await api.get('/pallets', { params: { tenantId, arquivados } })).data
    },
    enabled: !!tenantId,
  })

  // Painel "paletes abertos": a lista que responde por que a fila insiste num
  // palete. Só carrega quando aberta — é uma consulta por palete aberto.
  type Aberto = {
    id: string; code: string; openedAt: string | null; dias: number | null
    caixas: number; unidades: number; locationCode: string | null
    vazio: boolean; podeFechar: boolean; sugestao: string
    pendencias: { sku: string | null; caixas: number; unidades: number; unidadesPorCaixa: number | null }[]
  }
  const { data: abertos = [], isLoading: abertosLoading } = useQuery<Aberto[]>({
    queryKey: ['pallets-abertos', tenantId],
    queryFn: async () => (await api.get('/pallets/abertos', { params: { tenantId } })).data,
    enabled: !!tenantId && verAbertos,
  })

  async function downloadLabel(palletId: string, code: string) {
    setDownloading(palletId)
    try {
      const res = await api.get(`/pallets/${palletId}/label`, {
        params: { format: formatFilter },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `etiqueta-${code}-${formatFilter}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(null)
    }
  }

  async function downloadBatch() {
    if (selected.size === 0) return
    setDownloading('batch')
    try {
      const res = await api.get('/pallets/labels', {
        params: { ids: [...selected].join(','), format: formatFilter },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `paletes-${selected.size}-${formatFilter}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(null)
    }
  }

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Paletes</h2>
          <p className="text-sm text-gray-500">Rastreamento de paletes e caixas por localização</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" disabled={selected.size === 0}
            loading={downloading === 'batch'} onClick={downloadBatch}>
            <Download className="h-4 w-4" /> Imprimir {selected.size > 0 ? selected.size : ''} selecionado(s)
          </Button>
          <span className="text-sm text-gray-500">Formato:</span>
          <select className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
            value={formatFilter} onChange={e => setFormatFilter(e.target.value as '10x15' | 'a4')}>
            <option value="10x15">10×15cm (Zebra)</option>
            <option value="a4">A4 (4 por folha)</option>
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button variant={verAbertos ? 'primary' : 'secondary'} size="sm" onClick={() => setVerAbertos(v => !v)}>
          <Unlock className="h-4 w-4" /> Paletes abertos
        </Button>
        {!!vazios?.vazios && arquivados === 'nao' && (
          <Button variant="secondary" size="sm" loading={arqBusy === 'todos'} onClick={arquivarTodosVazios}
            title="Tira da lista os paletes sem nada dentro. O histórico fica, e dá para desarquivar.">
            <Archive className="h-4 w-4" /> Arquivar {vazios.vazios} vazio(s)
          </Button>
        )}
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          mostrar:
          <select className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
            value={arquivados} onChange={e => setArquivados(e.target.value as 'nao' | 'sim' | 'todos')}>
            <option value="nao">em circulação</option>
            <option value="sim">só arquivados</option>
            <option value="todos">todos</option>
          </select>
        </label>
        {estadoMsg && <span className="text-sm text-gray-700">{estadoMsg}</span>}
        {arqMsg && <span className="text-sm text-gray-700">{arqMsg}</span>}
      </div>

      {verAbertos && (
        <Card>
          <CardContent className="space-y-3 py-4">
            <div>
              <p className="text-sm font-semibold text-gray-900">Paletes abertos</p>
              <p className="text-xs text-gray-500">
                O abastecimento manda terminar o palete aberto antes de romper outro. Um palete que ficou
                aberto por engano fica eternamente no topo da fila — e alguém acaba abrindo um segundo do mesmo produto.
              </p>
            </div>
            {abertosLoading ? (
              <p className="text-sm text-gray-400">Carregando…</p>
            ) : abertos.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum palete aberto. A fila do abastecimento está limpa.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-100">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Palete</th>
                      <th className="px-3 py-2 text-left">Local</th>
                      <th className="px-3 py-2 text-right">Caixas</th>
                      <th className="px-3 py-2 text-right">Unidades</th>
                      <th className="px-3 py-2 text-left">Aberto há</th>
                      <th className="px-3 py-2 text-left">O que fazer</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {abertos.map(a => (
                      <tr key={a.id} className={a.vazio ? 'bg-amber-50/50' : ''}>
                        <td className="px-3 py-2 font-mono font-semibold text-gray-900">{a.code}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600">{a.locationCode ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{a.caixas || '—'}</td>
                        <td className="px-3 py-2 text-right">{a.unidades || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{a.dias == null ? '—' : a.dias === 0 ? 'hoje' : `${a.dias} dia(s)`}</td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {a.sugestao}
                          {a.pendencias.length > 0 && (
                            <span className="mt-0.5 flex items-start gap-1 text-amber-700">
                              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                              <span>
                                {a.pendencias.slice(0, 3).map(x =>
                                  `${x.sku ?? '?'}: ${x.caixas} cx × ${x.unidadesPorCaixa} = ${x.caixas * (x.unidadesPorCaixa ?? 0)} un, tem ${x.unidades}`
                                ).join(' · ')}
                              </span>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant={a.podeFechar ? 'primary' : 'secondary'}
                            loading={estadoBusy === a.id}
                            onClick={() => mudarEstado({ id: a.id, code: a.code, openedAt: a.openedAt }, 'fechar')}>
                            <Lock className="h-4 w-4" /> Fechar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : pallets.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">
              Nenhum palete registrado — crie paletes ao registrar um recebimento
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">
                      <input type="checkbox"
                        checked={pallets.length > 0 && selected.size === pallets.length}
                        onChange={e => setSelected(e.target.checked ? new Set(pallets.map(p => p.id)) : new Set())} />
                    </th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Código</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Tipo</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Localização</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Caixas</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Unidades</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Validade</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Entrada</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {pallets.map((p) => (
                    <Fragment key={p.id}>
                    <tr className={cn('hover:bg-gray-50', selected.has(p.id) && 'bg-blue-50/50', editing === p.id && 'bg-blue-50/60')}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                      </td>
                      <td className="px-5 py-3 font-mono font-bold text-gray-900">{p.code}</td>
                      <td className="px-5 py-3 text-gray-600">{typeLabel[p.type] ?? p.type}</td>
                      <td className="px-5 py-3 font-mono text-gray-700">
                        {p.locationCode
                          ?? (p.notes?.replace(/^Local:\s*/i, '').trim() || null)
                          ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-3 text-right font-semibold">{p.quantityBoxes || '—'}</td>
                      <td className="px-5 py-3 text-right font-semibold">{p.quantityUnits || '—'}</td>
                      <td className="px-5 py-3 text-gray-600">
                        {p.expiryDate ?? <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge variant={statusVariant[p.status] ?? 'default'}>
                            {statusLabel[p.status] ?? p.status}
                          </Badge>
                          {p.openedAt && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" title={`Aberto em ${new Date(p.openedAt).toLocaleString('pt-BR')}`}>
                              ABERTO
                            </span>
                          )}
                          {p.archivedAt && (
                            <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600" title={`Arquivado em ${new Date(p.archivedAt).toLocaleString('pt-BR')}`}>
                              ARQUIVADO
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-500">
                        {new Date(p.createdAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-0.5">
                          <Button variant="ghost" size="sm" loading={downloading === p.id}
                            title="Baixar etiqueta" onClick={() => downloadLabel(p.id, p.code)}>
                            <Download className="h-4 w-4" />
                          </Button>
                          {/* Abrir/fechar na linha: é a ação do dia a dia, e
                              estava escondida dentro da correção de contagem. */}
                          <Button variant="ghost" size="sm" loading={estadoBusy === p.id}
                            title={p.openedAt ? 'Fechar (lacrar) este palete' : 'Marcar como aberto'}
                            onClick={() => mudarEstado(p, p.openedAt ? 'fechar' : 'abrir')}>
                            {p.openedAt
                              ? <Lock className="h-4 w-4 text-amber-600" />
                              : <Unlock className="h-4 w-4 text-gray-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" title="Corrigir contagem"
                            onClick={() => openEdit(p.id)}>
                            <Pencil className={cn('h-4 w-4', editing === p.id && 'text-blue-600')} />
                          </Button>
                          {/* Arquivar em vez de apagar: o palete some da lista,
                              o histórico fica. Só vazio pode ser arquivado. */}
                          {p.archivedAt ? (
                            <Button variant="ghost" size="sm" title="Desarquivar — volta para a lista"
                              loading={arqBusy === p.id} onClick={() => arquivar(p, false)}>
                              <ArchiveRestore className="h-4 w-4 text-blue-600" />
                            </Button>
                          ) : (
                            <Button variant="ghost" size="sm"
                              title={p.quantityUnits > 0 || p.quantityBoxes > 0
                                ? 'Só palete vazio pode ser arquivado'
                                : 'Arquivar — sai da lista, histórico fica'}
                              loading={arqBusy === p.id}
                              disabled={p.quantityUnits > 0 || p.quantityBoxes > 0}
                              onClick={() => arquivar(p, true)}>
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" title="Apagar palete"
                            loading={deleting === p.id} onClick={() => removePallet(p)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    {editing === p.id && (
                      <tr className="bg-blue-50/30">
                        <td colSpan={10} className="px-5 py-4">
                          <p className="mb-1 text-sm font-semibold text-gray-800">Corrigir contagem — {p.code}
                            {p.openedAt && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">ABERTO</span>}
                          </p>
                          <p className="mb-3 text-xs text-gray-500">
                            Aqui se acerta a <b>contagem</b>. Para abrir ou fechar o palete use o cadeado na linha:
                            fechar confere se sobrou caixa começada e pede motivo quando sobrou — e fica no histórico do palete.
                          </p>
                          {editItems.length === 0 ? (
                            <p className="text-sm text-gray-400">Carregando itens…</p>
                          ) : (
                            <div className="space-y-3">
                              {editItems.map((it, idx) => (
                                <div key={it.id} className="flex flex-wrap items-end gap-3">
                                  <div className="min-w-[180px] flex-1">
                                    <span className="text-xs uppercase tracking-wide text-gray-400">Produto</span>
                                    <p className="font-medium text-gray-800">{it.productName}</p>
                                  </div>
                                  <div>
                                    <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Caixas</label>
                                    <input inputMode="numeric" value={it.boxes}
                                      onChange={e => setItem(idx, { boxes: e.target.value.replace(/[^\d]/g, '') })}
                                      className="h-10 w-24 rounded-lg border border-gray-300 px-3 text-center font-semibold focus:border-blue-500 focus:outline-none" />
                                  </div>
                                  <div>
                                    <label className="mb-1 block text-xs uppercase tracking-wide text-gray-400">Unidades (total)</label>
                                    <div className="relative">
                                      <input inputMode="numeric" value={it.units}
                                        onChange={e => setItem(idx, { units: e.target.value.replace(/[^\d]/g, '') })}
                                        className="h-10 w-32 rounded-lg border border-gray-300 px-3 pr-10 text-center font-semibold focus:border-blue-500 focus:outline-none" />
                                      <button type="button" onClick={() => setCalcFor(it.id)} aria-label="Calculadora"
                                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-blue-600 active:bg-blue-50">
                                        <CalcIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div className="flex gap-2 pt-1">
                                <Button size="sm" loading={saving} onClick={() => saveEdit(p.id)}>
                                  <Save className="h-4 w-4" /> Salvar correção
                                </Button>
                                <Button size="sm" variant="secondary" onClick={() => setEditing(null)}>Cancelar</Button>
                              </div>
                              <p className="text-xs text-gray-400">
                                <b>Unidades</b> = total do produto no palete (ex.: 131 cx × 24 + 1 cx × 19 = 3163 — use o 🧮).
                                <b> Caixas</b> = contagem física de caixas (ex.: 132). O total do palete é recalculado ao salvar.
                              </p>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {calcFor && (
        <Calculator title="Unidades (total)"
          initial={editItems.find(i => i.id === calcFor)?.units}
          onInsert={n => setEditItems(a => a.map(x => (x.id === calcFor ? { ...x, units: String(n) } : x)))}
          onClose={() => setCalcFor(null)} />
      )}
    </div>
  )
}
